import { Button, type ButtonProps } from "~/common/mui";
import StoreIcon from "@mui/icons-material/Store";
import { useTranslation } from "react-i18next";
import { openModal } from "~/common/utils/ModalService";
import { BuyFromSalesPartnerModal } from "~/features/salesPartnerStock/components/BuyFromSalesPartnerModal";
import type { ProductData } from "~/domains/product/types/ProductData";
import { trackBuyFromSalesPartnerButton } from "~/app/analytics/ga/ga";
import type { GAPageTypeName } from "~/features/search/models/SearchPageType";

export const BuyFromSalesPartnerButton = ({
  product,
  onClick: onClickExternal,
  pageType,
  quantity,
  ...props
}: Omit<ButtonProps, "children" | "startIcon"> & {
  product: ProductData;
  pageType: GAPageTypeName;
  quantity?: number;
}) => {
  const { onClick, icon, label } = useBuyFromSalesPartnerButtonProps({ onClickExternal, product, pageType, quantity });

  return (
    <Button startIcon={icon} onClick={onClick} fullWidth {...props}>
      {label}
    </Button>
  );
};

interface UseBuyFromSalesPartnerButtonProps {
  onClickExternal?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  product: ProductData;
  pageType: GAPageTypeName;
  quantity?: number;
}

export const useBuyFromSalesPartnerButtonProps = ({
  onClickExternal,
  product,
  pageType,
  quantity,
}: UseBuyFromSalesPartnerButtonProps) => {
  const { t } = useTranslation();

  const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClickExternal?.(e);
    trackBuyFromSalesPartnerButton(product.designation, product.sapMaterialNumber, pageType);
    openModal("buy-from-sales-partner-modal", (close) => (
      <BuyFromSalesPartnerModal onClose={close} product={product} pageType={pageType} quantity={quantity} />
    ));
  };

  return {
    key: "buy-from-sales-partner",
    icon: <StoreIcon />,
    testId: "button-buy-from-sales-partner",
    onClick,
    label: t("addToCart.salesPartner.button"),
  };
};
